/**
 * S7 — Across portfolios (/agg/across).
 *
 * Rebuilt whole-screen to the approved figure (Story #5648 build v2): the
 * edges of this portfolio — every ladder its entities are on, who must act,
 * and what other organisations shared in. It watches; it never advances a
 * ladder, so the header carries only an export.
 *
 * This is the one aggregator screen that spans EVERY membership the caller
 * holds, not just the active one — see services/api.ts's getAcrossPortfolios
 * for why the union must come from that fetcher and never be reconstructed
 * from a single membership's portfolio payload.
 */
import { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueries, useQuery } from '@tanstack/react-query'
import { Download } from 'lucide-react'
import { useAggregator } from '../../contexts/AggregatorContext'
import { useAggregatorAccessGate } from '../../hooks/useAggregatorAccessGate'
import AdminGate from '../../components/organisation/AdminGate'
import LoadingSkeleton from '../../components/shared/LoadingSkeleton'
import HelpPopover from '../../components/help/HelpPopover'
import { NextStepBanner, useDefaultLevels, useSetSpine } from '../../components/spine'
import {
  getAcrossPortfolios,
  getAggregatorDisclosures,
  getAggregatorEntities,
  getPortfolioBrief,
  getPortfolioOverlapFunding,
} from '../../services/api'
import { downloadBase64File } from '../../components/whitespace/PortfolioWatchPanel'
import AcrossStandingLine from '../../components/aggregator/AcrossStandingLine'
import AcrossDisclosures from '../../components/aggregator/AcrossDisclosures'
import CrossFundDeals from '../../components/aggregator/CrossFundDeals'
import SharedIntoPortfolio from '../../components/aggregator/SharedIntoPortfolio'
import DuplicateFundingAsks from '../../components/aggregator/DuplicateFundingAsks'
import { buildDisclosureRows, countEntitiesOnLadders } from '../../components/aggregator/disclosureRows'
import { entityGroupPath } from '../../components/aggregator/entityGroups'
import type { DisclosureRow } from '../../components/aggregator/disclosureRows'
import type { AcrossPortfoliosResponse } from '../../types/whitespace'
import type { DisclosureAgreement } from '../../types'

/**
 * The one next step at Across portfolios: the first ladder where a consent is
 * owed by one of this portfolio's own entities, then the first NDA gate
 * waiting on one. Nothing else on this level is ever "next" — the portfolio
 * cannot advance a ladder itself.
 */
export function rankCrossPortfolioNextStep(rows: DisclosureRow[]): DisclosureRow | null {
  return (
    rows.find((row) => row.status === 'Consent owed') ??
    rows.find((row) => row.status === 'NDA pending') ??
    null
  )
}

export default function AcrossPortfolios() {
  const { hasAggregatorAccess, activeAggregatorId, memberships } = useAggregator()
  const accessGate = useAggregatorAccessGate('across portfolios')
  const navigate = useNavigate()
  const [exportState, setExportState] = useState<'idle' | 'working' | 'failed'>('idle')
  // The notches this viewer's ROLE gets — never a list this page decides.
  const levels = useDefaultLevels()

  const portfolioName =
    memberships.find((m) => m.aggregator_id === activeAggregatorId)?.aggregator_name ?? ''

  const {
    data: across,
    isLoading: acrossLoading,
    error: acrossError,
    refetch: refetchAcross,
  } = useQuery<AcrossPortfoliosResponse>({
    queryKey: ['aggregator-across', activeAggregatorId],
    queryFn: () => getAcrossPortfolios(activeAggregatorId ?? ''),
    enabled: hasAggregatorAccess && !!activeAggregatorId,
  })

  const acrossMemberships = useMemo(() => across?.aggregators ?? [], [across])

  const entityQueries = useQueries({
    queries: acrossMemberships.map((m) => ({
      queryKey: ['aggregator-entities', m.aggregator_id],
      queryFn: () => getAggregatorEntities(m.aggregator_id),
      enabled: hasAggregatorAccess,
    })),
  })
  const disclosureQueries = useQueries({
    queries: acrossMemberships.map((m) => ({
      queryKey: ['aggregatorDisclosures', m.aggregator_id],
      queryFn: () => getAggregatorDisclosures(m.aggregator_id),
      enabled: hasAggregatorAccess,
    })),
  })
  const nestedLoading =
    acrossMemberships.length > 0 &&
    (entityQueries.some((q) => q.isLoading) || disclosureQueries.some((q) => q.isLoading))

  const { data: funding } = useQuery({
    queryKey: ['portfolio-overlap-funding', activeAggregatorId],
    queryFn: () => getPortfolioOverlapFunding(activeAggregatorId),
    enabled: hasAggregatorAccess && !!activeAggregatorId,
  })

  const entityNameById = useMemo(() => {
    const map = new Map<string, string>()
    for (const q of entityQueries) {
      for (const e of q.data?.entities ?? []) map.set(e.entity_id, e.entity_name)
    }
    return map
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityQueries.map((q) => q.dataUpdatedAt).join(',')])

  const entityIds = useMemo(() => Array.from(entityNameById.keys()), [entityNameById])

  // Union by agreement_id — the same disclosure reachable through more than
  // one membership is deduplicated, never counted or listed twice.
  const disclosures = useMemo(() => {
    const byId = new Map<string, DisclosureAgreement>()
    for (const q of disclosureQueries) {
      for (const d of q.data ?? []) byId.set(d.agreement_id, d)
    }
    return Array.from(byId.values())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disclosureQueries.map((q) => q.dataUpdatedAt).join(',')])

  const rows = useMemo(
    () => buildDisclosureRows(disclosures, { entityNameById }),
    [disclosures, entityNameById],
  )
  const nextStep = rankCrossPortfolioNextStep(rows)

  useSetSpine(
    hasAggregatorAccess
      ? {
          level: 'across',
          levels,
          youreAt: [portfolioName].filter(Boolean),
          stepsWaiting: nextStep ? 1 : 0,
          zoomIn: { title: 'Zoom in: Portfolio', to: '/agg?view=overview' },
          zoomOut: { title: 'Widest view. Cross-fund views are always each side’s own screen.' },
          crumb: [portfolioName || 'Portfolio', 'Across portfolios'],
        }
      : null,
  )

  const handleExport = () => {
    setExportState('working')
    getPortfolioBrief(activeAggregatorId)
      .then((brief) => {
        downloadBase64File(brief.docx_base64, brief.filename, brief.content_type)
        setExportState('idle')
      })
      .catch(() => setExportState('failed'))
  }

  if (accessGate) return <div data-part="AcrossPortfolios">{accessGate}</div>

  if (acrossError) {
    return (
      <div data-part="AcrossPortfolios">
        <AdminGate
          area="across portfolios"
          orgName={portfolioName || undefined}
          error={acrossError}
          onRetry={() => void refetchAcross()}
        />
      </div>
    )
  }

  if (acrossLoading || nestedLoading) {
    return (
      <div data-part="AcrossPortfolios">
        <LoadingSkeleton rows={4} type="list" />
      </div>
    )
  }

  return (
    <div data-part="AcrossPortfolios">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-bold tracking-tight text-iflow-900">Across portfolios</h1>
            <HelpPopover topic="agg-across-portfolios" size={18} />
          </div>
          <p className="mt-1 text-sm text-slate-600">
            The edges of your portfolio — every ladder your entities are on, who must act, and what
            others shared in. Only what they have consented to share, at the rung they chose.
          </p>
        </div>
        <div className="flex shrink-0 flex-col items-end gap-1">
          <button
            type="button"
            onClick={handleExport}
            disabled={exportState === 'working'}
            title="No primary action at this level — it watches; it never advances a ladder"
            className="inline-flex items-center gap-1.5 rounded-lg border border-iflow-900/15 bg-white px-3 py-2 text-[13px] font-semibold text-iflow-800 disabled:opacity-50"
          >
            <Download size={14} aria-hidden />{' '}
            {exportState === 'working' ? 'Building the brief…' : 'Export portfolio brief (.docx)'}
          </button>
          {exportState === 'failed' && (
            <p data-testid="brief-export-error" className="text-[11.5px] text-amber-700">
              The brief could not be built. Please try again.
            </p>
          )}
        </div>
      </div>

      <AcrossStandingLine />

      {nextStep ? (
        <NextStepBanner
          at="Across portfolios"
          verb="consent"
          headline={
            nextStep.status === 'NDA pending'
              ? `${nextStep.counterpartyLabel} is at the NDA gate — it is waiting on ${nextStep.entityLabel}.`
              : `${nextStep.counterpartyLabel} has consented — the next rung is waiting on ${nextStep.entityLabel}.`
          }
          rule="from the ladder"
          tone="amber"
          cta={{
            label: `Open ${nextStep.entityLabel} →`,
            to: entityGroupPath(nextStep.entityLabel),
          }}
        />
      ) : (
        <NextStepBanner
          at="Across portfolios"
          verb="consent"
          none
          headline="No consent is owed on any of your entities' ladders right now."
          rule="from the ladder · only the entity can consent"
        />
      )}

      <div className="grid grid-cols-1 items-start gap-4 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <AcrossDisclosures
            rows={rows}
            entityCount={countEntitiesOnLadders(disclosures, new Set(entityIds))}
          />
        </div>
        <div className="flex flex-col gap-4">
          <CrossFundDeals disclosures={disclosures} entityIds={entityIds} />
          <SharedIntoPortfolio sources={across?.shared_in_sources ?? []} />
          <DuplicateFundingAsks
            rollup={funding}
            onOpenGroup={(groupId) =>
              navigate(groupId ? `/agg?view=overlap&group=${groupId}` : '/agg?view=overlap')
            }
          />
        </div>
      </div>
    </div>
  )
}
